type CloudflareLoaderParams = {
  src: string;
  width: number;
  quality?: number;
};

const normalizeSrc = (src: string) => {
  return src.startsWith("/") ? src.slice(1) : src;
};

function getBasePath(): string {
  // NEXT_PUBLIC_BASE_PATH is set in next.config.ts to mirror the configured basePath
  return process.env.NEXT_PUBLIC_BASE_PATH || "";
}

function appendSizingParams(
  src: string,
  width: number,
  quality: number | undefined,
  widthParam: "w" | "size" = "w",
) {
  const url = new URL(src);
  url.searchParams.set(widthParam, String(width));
  url.searchParams.set("q", String(quality || 75));
  return url.toString();
}

export default function cloudflareLoader({
  src,
  width,
  quality,
}: CloudflareLoaderParams) {
  const basePath = getBasePath();

  // If the src is an absolute URL, do not modify it with basePath
  const isAbsoluteUrl = /^https?:\/\//i.test(src);

  const isUnsplash =
    isAbsoluteUrl &&
    (() => {
      try {
        const url = new URL(src);
        return (
          url.hostname === "images.unsplash.com" ||
          url.hostname.endsWith(".unsplash.com")
        );
      } catch {
        return false;
      }
    })();

  if (isUnsplash) {
    const url = new URL(appendSizingParams(src, width, quality));
    url.searchParams.set("fit", "crop");
    return url.toString();
  }

  // Skip Cloudflare optimization for external services that don't allow proxying
  const isDicebear =
    isAbsoluteUrl &&
    (() => {
      try {
        const url = new URL(src);
        return (
          url.hostname === "api.dicebear.com" ||
          url.hostname.endsWith(".dicebear.com")
        );
      } catch {
        return false;
      }
    })();

  const isPicsum =
    isAbsoluteUrl &&
    (() => {
      try {
        const url = new URL(src);
        return (
          url.hostname === "picsum.photos" ||
          url.hostname.endsWith(".picsum.photos")
        );
      } catch {
        return false;
      }
    })();

  const skipCloudflareOptimization =
    isAbsoluteUrl &&
    (() => {
      try {
        const url = new URL(src);
        return (
          isDicebear ||
          isPicsum ||
          url.hostname === "spond-storage-production.s3.amazonaws.com" ||
          url.hostname === "maps.googleapis.com"
        );
      } catch {
        return false;
      }
    })();

  if (process.env.NODE_ENV === "development" || skipCloudflareOptimization) {
    if (isAbsoluteUrl) {
      if (isDicebear) {
        return appendSizingParams(src, width, quality, "size");
      }
      if (isPicsum) {
        return appendSizingParams(src, width, quality);
      }
      return src;
    }
    // Ensure local assets include basePath during development
    return `${basePath}${src}`;
  }

  const params = [`width=${width}`, `quality=${quality || 75}`, "format=auto"];

  // Opt-in: components can append `?_cfRatio=W/H` to request server-side
  // saliency-aware cropping at the given target aspect ratio.
  let resourceSrc = src;
  try {
    const probe = new URL(src, isAbsoluteUrl ? undefined : "http://_");
    const ratioParam = probe.searchParams.get("_cfRatio");
    if (ratioParam) {
      const ratio = Number.parseFloat(ratioParam);
      if (Number.isFinite(ratio) && ratio > 0) {
        params.push("gravity=auto", `height=${Math.round(width / ratio)}`);
      }
      probe.searchParams.delete("_cfRatio");
      const search = probe.searchParams.toString();
      const cleanedPath = `${probe.pathname}${search ? `?${search}` : ""}`;
      resourceSrc = isAbsoluteUrl
        ? `${probe.protocol}//${probe.host}${cleanedPath}`
        : cleanedPath;
    }
  } catch {
    // src isn't parseable as a URL — leave untouched
  }

  // For production, prepend basePath for local asset paths so Cloudflare fetches from the correct origin path
  const resourcePath = isAbsoluteUrl
    ? resourceSrc
    : `${normalizeSrc(basePath)}/${normalizeSrc(resourceSrc)}`;

  return `/cdn-cgi/image/${params.join(",")}/${resourcePath}`;
}
